/**
 * Mux Data environment key resolution.
 *
 * **Primary integration (Option A — first-party HTML):** do not put the key in asvp.js. Build or
 * serve the page so `MUX_KEY_RUNTIME` / `window.__ASVP_MUX_KEY__` is emitted only when the server
 * knows analytics consent applies (cookie, edge, SSR). The webpack HtmlWebpackPlugin injects
 * MUX_KEY from CI for static builds; for consent-aware pages, use a server-rendered template or
 * omit the inline script until consent. `resolveMuxEnvironmentKey()` still requires
 * `shouldEnableMux()` before TOP receives the key.
 *
 * **Fallback:** `sessionStorage` after a successful play with key from window, or
 * `setMuxEnvironmentKeyForSession` for embed-only hosts.
 *
 * **Production:** MUX_KEY is not embedded in asvp.js (DefinePlugin empty) so dependencies cannot read the key at load time.
 * **Development:** the webpack dev build may embed MUX_KEY from `process.env` so Mux still resolves after reload.
 *
 * If `enabled: true` is passed with an empty `environmentKey`, TOP’s Mux SDK still targets a
 * litix host and defaults the subdomain to `inferred` — those requests are still Mux beacons.
 */

import type { MuxConfig } from '@top/player-block-web';

import { shouldEnableMux } from './muxConsent';

/** Survives reload in the same tab; cleared when the tab closes. */
export const SESSION_STORAGE_MUX_KEY = 'asvp_mux_environment_key';

/** Pass at setup and when consent denies — enabled: false, no environment key. */
export function muxDisabledConfig(): MuxConfig {
    return {
        playerName: 'top_web',
        playerVersion: process.env.VERSION,
        debug: false,
        enabled: false,
        environmentKey: '',
        metadata: { subPropertyId: 'adult_swim' },
    };
}

declare global {
    interface Window {
        __ASVP_MUX_KEY__?: string;
    }
}

function readSessionStoredMuxKey(): string {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return '';
        }
        const v = window.sessionStorage.getItem(SESSION_STORAGE_MUX_KEY);
        return v && v.trim() !== '' ? v.trim() : '';
    } catch {
        return '';
    }
}

function writeSessionStoredMuxKey(key: string): void {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage && key) {
            window.sessionStorage.setItem(SESSION_STORAGE_MUX_KEY, key);
        }
    } catch {
        // noop
    }
}

/**
 * Host can call after consent with the env key (e.g. from config loaded only when CMP allows).
 * Sets window + sessionStorage so reload still resolves the key before player init.
 */
export function setMuxEnvironmentKeyForSession(key: string): void {
    const trimmed = key && String(key).trim();
    if (!trimmed) {
        return;
    }
    if (typeof window !== 'undefined') {
        window.__ASVP_MUX_KEY__ = trimmed;
    }
    writeSessionStoredMuxKey(trimmed);
}

/**
 * When consent allows Mux and the key came from HTML/window, persist it for the next reload.
 */
export function syncMuxKeySessionStorageIfConsented(): void {
    if (!shouldEnableMux()) {
        return;
    }
    if (typeof window === 'undefined' || !window.__ASVP_MUX_KEY__) {
        return;
    }
    const k = String(window.__ASVP_MUX_KEY__).trim();
    if (k && readSessionStoredMuxKey() !== k) {
        writeSessionStoredMuxKey(k);
    }
}

/** Key from runtime (dev bundle may embed MUX_KEY; production bundle does not). */
export function getRawMuxEnvironmentKey(): string {
    const fromBundle = process.env.MUX_KEY ?? '';
    console.log('getRawMuxEnvironmentKey: fromBundle', fromBundle);
    if (fromBundle) {
        return fromBundle;
    }
    if (typeof window !== 'undefined' && window.__ASVP_MUX_KEY__) {
        return String(window.__ASVP_MUX_KEY__).trim();
    }
    const fromSession = readSessionStoredMuxKey();
    if (fromSession) {
        return fromSession;
    }
    return '';
}

/** Non-empty only when consent allows Mux and a key exists — TOP never receives the key otherwise. */
export function resolveMuxEnvironmentKey(): string {
    const raw = getRawMuxEnvironmentKey();
    console.log('resolveMuxEnvironmentKey', raw, shouldEnableMux());
    if (!raw || !shouldEnableMux()) {
        return '';
    }
    return raw;
}

/** Lengths only — for `[asvp mux]` debug (no key material). */
export function getMuxKeySnapshotForDebug(): {
    hasRawKey: boolean;
    rawKeyLength: number;
    resolvedKeyLength: number;
    hasSessionStorageKey: boolean;
} {
    const raw = getRawMuxEnvironmentKey();
    const resolved = resolveMuxEnvironmentKey();
    let hasSessionStorageKey = false;
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            const v = window.sessionStorage.getItem(SESSION_STORAGE_MUX_KEY);
            hasSessionStorageKey = !!(v && v.trim());
        }
    } catch {
        // noop
    }
    return {
        hasRawKey: raw.length > 0,
        rawKeyLength: raw.length,
        resolvedKeyLength: resolved.length,
        hasSessionStorageKey,
    };
}

/**
 * Console-friendly diagnostics (lengths only, no key material). Use when
 * `__ASVP_MUX_KEY__` / sessionStorage look empty — confirms whether any source has a key and if
 * consent blocks resolution.
 */
export function getMuxKeyDiagnostics(): {
    sessionStorageItemName: string;
    windowKeyLength: number;
    sessionStorageKeyLength: number;
    embeddedBundleKeyLength: number;
    rawKeyLength: number;
    resolvedKeyLength: number;
    shouldEnableMux: boolean;
    summary: string;
} {
    const bundleKey = process.env.MUX_KEY ?? '';
    let sessionLen = 0;
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            const v = window.sessionStorage.getItem(SESSION_STORAGE_MUX_KEY);
            sessionLen = v && v.trim() !== '' ? v.trim().length : 0;
        }
    } catch {
        // noop
    }
    const winLen =
        typeof window !== 'undefined' && window.__ASVP_MUX_KEY__
            ? String(window.__ASVP_MUX_KEY__).trim().length
            : 0;
    const raw = getRawMuxEnvironmentKey();
    const resolved = resolveMuxEnvironmentKey();
    const consent = shouldEnableMux();

    let summary: string;
    if (raw.length === 0) {
        summary =
            'No key anywhere: HTML did not set window.__ASVP_MUX_KEY__, sessionStorage is empty, and the bundle has no MUX_KEY. The host must inject the key (SSR/edge) or call ASVP.setMuxEnvironmentKeyForSession(key) after consent.';
    } else if (resolved.length === 0) {
        summary =
            'A raw key exists (window/session/bundle) but shouldEnableMux() is false — consent APIs block passing it to TOP until the user consents.';
    } else {
        summary = 'Key is available for Mux config when setup/play runs.';
    }

    return {
        sessionStorageItemName: SESSION_STORAGE_MUX_KEY,
        windowKeyLength: winLen,
        sessionStorageKeyLength: sessionLen,
        embeddedBundleKeyLength: String(bundleKey).trim().length,
        rawKeyLength: raw.length,
        resolvedKeyLength: resolved.length,
        shouldEnableMux: consent,
        summary,
    };
}
